import { useCallback, useMemo } from "react";
import { useInvalidateMCPConnections } from "./hooks/useMCPQueryScope";
import { cn } from "@/utils/cn";
import {
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
  useQueryStates,
} from "nuqs";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import AddConnectorDialog from "./components/AddConnectorDialog";
import DeleteConnectionDialog from "./components/DeleteConnectionDialog";
import MCPDetailPanel from "./components/MCPDetailPanel";
import MCPList from "./components/MCPList";
import MCPToolbar from "./components/MCPToolbar";
import { mapDtoToFormValues, mapFormToCreatePayload } from "./adapters";
import {
  openOAuthPopup,
  settleOAuthPopup,
  watchOAuthPopupCompletion,
} from "./oauth";
import { useCreateMCPConnection } from "./hooks/useCreateMCPConnection";
import { useDeleteMCPConnection } from "./hooks/useDeleteMCPConnection";
import { useMCPConnection } from "./hooks/useMCPConnection";
import {
  useMCPConnections,
} from "./hooks/useMCPConnections";
import { useUpdateMCPConnection } from "./hooks/useUpdateMCPConnection";
import { useUsers } from "./hooks/useUsers";
import { usePanePresence } from "./usePanePresence";
import type { ConnectorFormValues } from "./types";
import { canManageMcpConnection } from "./connectionAccess";
// AuthContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useAuth } from "../../contexts/AuthContext";

// The page has exactly one "active surface" at a time. Keeping that in the URL
// gives us:
//   - a single source of truth (no juggling 4 useStates)
//   - back-button + shareable links for free
//   - mutual exclusion by construction (no two overlays fighting)
const VIEW_VALUES = ["drawer", "edit", "create", "delete"] as const;
type View = (typeof VIEW_VALUES)[number];

const MCPsPage = () => {
  const { t } = useTranslation();
  const invalidateConnections = useInvalidateMCPConnections();
  const { connections, isLoading, error } = useMCPConnections();
  const { data: users = [] } = useUsers();
  const { userProfile, currentUser } = useAuth();

  const selfId: string | undefined = userProfile?.uid ?? currentUser?.id;
  const selfEmail: string | undefined = (
    userProfile?.email ?? currentUser?.email
  )?.toLowerCase();
  const currentOrgUser = useMemo(() => {
    const byEmail = selfEmail
      ? users.find((user) => user.email?.toLowerCase() === selfEmail)
      : undefined;
    return byEmail ?? users.find((user) => selfId && user.id === selfId);
  }, [selfEmail, selfId, users]);

  const manageableConnectionIds = useMemo(
    () =>
      new Set(
        connections
          .filter((connection) =>
            canManageMcpConnection(connection.sharing, currentOrgUser),
          )
          .map((connection) => connection.id),
      ),
    [connections, currentOrgUser],
  );
  const canManage = useCallback(
    (connectionId: string) => manageableConnectionIds.has(connectionId),
    [manageableConnectionIds],
  );

  const [query, setQuery] = useQueryState("q", parseAsString.withDefault(""));
  const [{ view, id }, setUrlState] = useQueryStates({
    view: parseAsStringLiteral(VIEW_VALUES),
    id: parseAsString,
  });

  // Derive every surface's state from the URL. No local copies.
  const selectedId = view === "drawer" ? id : null;
  const editingId = view === "edit" ? id : null;
  const pendingDeleteId = view === "delete" ? id : null;
  const isAddOpen = view === "create";
  const authorizedEditingId = editingId && canManage(editingId) ? editingId : null;
  const dialogOpen = view === "create" || Boolean(authorizedEditingId);
  const dialogMode: "create" | "edit" =
    view === "edit" ? "edit" : "create";

  // Pulls the row from the shared cache so the dialog can pre-fill instantly
  // when the user picks Edit from the list menu.
  const { dto: editingDto } = useMCPConnection(authorizedEditingId);

  const closeAll = useCallback(() => {
    setUrlState({ view: null, id: null });
  }, [setUrlState]);

  const openView = useCallback(
    (next: View, targetId: string | null = null) => {
      setUrlState({ view: next, id: targetId });
    },
    [setUrlState],
  );

  const createMutation = useCreateMCPConnection({
    onSuccess: () => {
      if (isAddOpen) closeAll();
    },
  });
  const updateMutation = useUpdateMCPConnection({
    onSuccess: () => {
      if (view === "edit") closeAll();
    },
  });
  const {
    mutate: updateListStatus,
    isPending: isListStatusPending,
    variables: listStatusVariables,
  } = useUpdateMCPConnection();
  const deleteMutation = useDeleteMCPConnection({
    onSuccess: (deletedId) => {
      // If anything else was pointing at the same row, close it.
      if (id === deletedId) closeAll();
    },
  });

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return connections;
    return connections.filter((connection) =>
      connection.name.toLowerCase().includes(trimmed),
    );
  }, [query, connections]);

  const pendingDeleteConnection = useMemo(
    () =>
      pendingDeleteId
        ? connections.find(
            (connection) =>
              connection.id === pendingDeleteId && canManage(connection.id),
          ) ?? null
        : null,
    [pendingDeleteId, connections, canManage],
  );

  const editInitialValues = useMemo(() => {
    if (!editingDto) return undefined;
    try {
      return mapDtoToFormValues(editingDto);
    } catch (err) {
      console.error("[MCPs] mapDtoToFormValues threw", err, "dto:", editingDto);
      return undefined;
    }
  }, [editingDto]);

  const dialogSubmitting =
    dialogMode === "edit"
      ? updateMutation.isPending
      : createMutation.isPending;

  const handleSelect = useCallback(
    (rowId: string) => openView("drawer", rowId),
    [openView],
  );
  const handleEdit = useCallback(
    (rowId: string) => {
      if (canManage(rowId)) openView("edit", rowId);
    },
    [canManage, openView],
  );
  const handleDelete = useCallback(
    (rowId: string) => {
      if (canManage(rowId)) openView("delete", rowId);
    },
    [canManage, openView],
  );
  const handleNewConnection = useCallback(
    () => openView("create"),
    [openView],
  );
  const handleToggleActive = useCallback(
    (connectionId: string, isActive: boolean) => {
      if (!canManage(connectionId) || isListStatusPending) return;
      updateListStatus({
        id: connectionId,
        payload: { is_active: isActive },
      });
    },
    [canManage, isListStatusPending, updateListStatus],
  );

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteId && canManage(pendingDeleteId)) {
      deleteMutation.mutate(pendingDeleteId);
    }
  }, [pendingDeleteId, canManage, deleteMutation]);

  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if (!next) closeAll();
    },
    [closeAll],
  );

  const handleDialogSubmit = useCallback(
    (values: ConnectorFormValues) => {
      const payload = mapFormToCreatePayload(values);
      if (dialogMode === "edit" && authorizedEditingId) {
        updateMutation.mutate({ id: authorizedEditingId, payload });
        return;
      }

      // An unauthorized edit URL must never fall through into create.
      if (dialogMode === "edit") return;

      // Pre-open the OAuth tab synchronously inside the submit click so the
      // browser allows it (blockers reject window.open after the await).
      const popup = values.authHeader === "oauth" ? openOAuthPopup() : null;

      createMutation.mutate(payload, {
        onResourceSuccess: (response, handoff) => {
          if (popup?.closed) return;
          const authorizationUrl = response.oauth_init?.authorization_url;
          const { redirected, blocked } = settleOAuthPopup(
            popup,
            authorizationUrl,
          );
          if (redirected) {
            handoff();
            watchOAuthPopupCompletion(popup!, authorizationUrl!, () => {
              void invalidateConnections();
            });
            toast.success(
              t("mcpPage.toasts.addedAuthorize"),
            );
          } else if (blocked && authorizationUrl) {
            toast.warning(
              t("mcpPage.toasts.popupBlocked", { url: authorizationUrl }),
            );
          }
        },
        // This handle belongs to the operation, not the currently open dialog.
        // Cleanup must survive scope cancellation and page unmount.
        onCleanup: () => {
          if (popup && !popup.closed) popup.close();
        },
      });
    },
    [
      dialogMode,
      authorizedEditingId,
      updateMutation,
      createMutation,
      invalidateConnections,
      t,
    ],
  );

  // renderedId lags selectedId on close so the pane can animate out before it
  // leaves the tree.
  const { renderedId, closing, onExited } = usePanePresence(selectedId);
  const detailOpen = Boolean(renderedId);

  return (
    <>
      {/*
        Two panes filling the content area, not a panel floating next to a list.
        The divider is the left pane's trailing border, so there is exactly one
        line and it sits between them; it only appears when detail is open,
        otherwise it would draw down the far right edge of an empty row.

        Each pane scrolls on its own (min-h-0 + overflow-y-auto). The page itself
        never scrolls, which is what keeps both headers fixed in place while a
        long tool list moves underneath.
      */}
      <div className="flex h-full min-h-0 overflow-hidden">
        <div
          className={cn(
            "flex min-w-0 flex-1 flex-col",
            detailOpen && "border-e border-border",
          )}
        >
          <MCPToolbar
            query={query}
            onQueryChange={setQuery}
            onNewConnection={handleNewConnection}
          />

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-4">
            <MCPList
              connections={filtered}
              selectedId={selectedId}
              canManage={canManage}
              onSelect={handleSelect}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
              updatingActiveId={
                isListStatusPending ? listStatusVariables?.id : null
              }
              isLoading={isLoading}
              errorMessage={error ? t("mcpPage.errors.loadConnections") : null}
            />
          </div>
        </div>

        {renderedId && (
          // The animation is on mount and on the closing flag, so it plays when
          // the pane opens and closes but not when the user moves between rows —
          // the wrapper stays mounted while only connectionId changes. Opening is
          // a change of layout; switching is a change of content.
          <div
            className={cn(
              "min-w-0 flex-1 overflow-y-auto",
              closing ? "animate-pane-out" : "animate-pane-in",
            )}
            // Animations inside the pane bubble up here too, and any one of them
            // would unmount it mid-open.
            onAnimationEnd={(event) => {
              if (event.target === event.currentTarget && closing) onExited();
            }}
          >
            <MCPDetailPanel
              onClose={closeAll}
              onEdit={() => handleEdit(renderedId)}
              onDelete={() => handleDelete(renderedId)}
              connectionId={renderedId}
              currentOrgUser={currentOrgUser}
            />
          </div>
        )}
      </div>

      <AddConnectorDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={editInitialValues}
        onOpenChange={handleDialogOpenChange}
        onSubmit={handleDialogSubmit}
        isSubmitting={dialogSubmitting}
      />

      <DeleteConnectionDialog
        connection={pendingDeleteConnection}
        isDeleting={deleteMutation.isPending}
        onCancel={closeAll}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default MCPsPage;
